import { useMemo, useRef, useState } from "react";
import {
  FileText,
  Zap,
  Eye,
  Download,
  Paperclip,
  RefreshCw,
  Clock,
  CheckCircle2,
  PenLine,
  ShieldAlert,
} from "@semo/icons";
import { cn, Button } from "@semo/shared-ui";
import { instance as apiClient } from "@semo/api-contracts";
import { toast } from "sonner";
import { useChatActions } from "../../context/ChatActionContext";
import type { ChatRole } from "../types";
import { FilePreviewModal } from "../atoms/FilePreviewModal";
import {
  ContractRestrictionAllNoModal,
  CONTRACT_RESTRICTION_QUESTIONS,
} from "../modals/ContractRestrictionAllNoModal";
import { ContractRestrictionBuyerConfirmModal } from "../modals/ContractRestrictionBuyerConfirmModal";
import { OptionalRosterModal } from "../modals/OptionalRosterModal";
import {
  ContractSettlementBadgeChip,
  resolveContractSettlementBadge,
} from "./contract-settlement-status";

// 백엔드 cmarket/domain/types/contract-document.types.ts 와 동기화
const CONTRACT_RESTRICTION_PAPER_CODE = 135;
const RETIREE_ROSTER_PAPER_CODES = new Set([164, 188, 195, 222]);

/**
 * [계약 서류 관리] 카드
 *
 * DOCUMENT_REQUEST 메시지의 status='requested' 일 때 렌더된다.
 * - [자동 생성] 섹션: buyer/supplier 모두 미리보기·다운로드
 * - [직접 제출] 섹션:
 *   · buyer: 대기 or 완료 상태 표시 + 완료 시 미리보기·다운로드
 *   · supplier: 대기 시 [파일 첨부] 버튼 / 완료 시 [미리보기][다운로드][재첨부]
 * - Footer: 전체 완료된 서류 일괄 다운로드
 */

export interface ContractDocumentItem {
  id: string;
  paperCode: number;
  name: string;
  kind: "auto" | "auto_pending_input" | "manual";
  status: "pending" | "completed";
  fileUrl?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
  contentType?: string | null;
  uploadedAt?: string | null;
}

interface ContractDocumentManagementCardProps {
  roomId: string;
  role: ChatRole;
  items: ContractDocumentItem[];
  /** 계약 ID — 결제 CTA 를 씨마켓 '나의 계약 관리' 로 넘길 때 쓴다. */
  contractId: string;
  /**
   * 계약 시 고른 결제 수단 (contracts.payment_channel).
   * null 이면 결제 CTA 자체를 그리지 않는다 — 컬럼 신설(2026-08-04) 이전의 레거시
   * 선결제 계약이 전부 null 이라, 이미 대금을 수납한 건에 결제 버튼이 뜨는 것을 막는다.
   *
   * (종전엔 견적요청의 prepaid/postpaid 로 판단했으나, 2026-08-04 에 신규 계약이 전량
   *  후불로 확정되면서 그 축은 상시 참인 죽은 게이트가 됐다.)
   */
  paymentChannel?: "cash" | "card" | null;
  /** 결제 완료 여부 (contracts.paid_at 유무). ContractCompleteCard 와 같은 판정. */
  isPaid?: boolean;
  /**
   * 씨마켓 계산서 진행 상태. 현금 계약에서 `isPaid` 와 같은 자리를 맡는다.
   * 버튼과 달리 **역할을 가리지 않고 표시한다** — 공급사도 발행 여부를 알아야 한다.
   */
  taxInvoiceStatus?: "REQUESTED" | "ISSUED" | "CANCELLED" | null;
  /**
   * 결제 CTA 클릭 — 씨마켓 '나의 계약 관리' 로 데려가 달라고 부모(씨마켓)에게 알린다.
   * 세모는 대금을 처리하지 않는다. buyer 앱이 임베드 안에서만 주입하므로,
   * 벤더 앱·임베드 밖에서는 undefined 라 버튼이 그려지지 않는다.
   */
  onOpenCmarketContracts?: (
    contractId: string,
    channel: "cash" | "card",
  ) => void;
  /**
   * paperCode 135(수의계약체결제한여부확인서) 제출 스냅샷 (backend metadata → DTO).
   * flaggedIndexes 가 비어있지 않으면 카드 상단에 경고 배너를 노출한다. 계약은 차단하지 않는다.
   */
  restrictionConfirm?: ContractRestrictionConfirmSnapshot;
}

export interface ContractRestrictionConfirmSnapshot {
  answers: string[];
  flaggedIndexes: number[];
  submittedAt: string;
  /**
   * 발주기관 최종 확인 응답. null 이고 flaggedIndexes 가 비어있지 않으면 buyer 확인 대기 상태
   * (buyer 카드에 [확인하기] CTA 노출). 값이 채워지면 최종 응답을 배너에 표시한다.
   */
  buyerConfirmation: "YES" | "NO" | null;
  /** buyer 확인 제출 시각. 미제출이면 null. */
  confirmedAt: string | null;
}

// SSOT — 백엔드 FilenameDomainService 확장자 화이트리스트와 동일 25 종
const ALLOWED_EXTENSIONS = [
  "pdf",
  "hwp",
  "hwpx",
  "hwt",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "cell",
  "ppt",
  "pptx",
  "show",
  "txt",
  "csv",
  "zip",
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "tiff",
  "tif",
  // 기존 채팅 첨부 정책과 일관 (backend 에도 여전히 허용됨)
  "rar",
  "7z",
  "webp",
];
const ACCEPT_ATTR = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",");
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_FILENAME_LENGTH = 255;

function humanFileSize(size: number | null | undefined): string {
  if (!size || size <= 0) return "";
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
  return `${(size / 1024 / 1024).toFixed(1)}MB`;
}

function triggerDownload(fileUrl: string, fileName?: string | null): void {
  const anchor = document.createElement("a");
  anchor.href = fileUrl;
  if (fileName) anchor.download = fileName;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

/**
 * URL/path 문자열에서 확장자 추출. 쿼리(`?`)·프래그먼트(`#`) 를 걷어낸 뒤 파일명 뒷부분을 잘라 소문자화.
 * c-market 이 내려주는 signed URL 처럼 `?token=...` 이 붙는 케이스에서 확장자 판별을 신뢰성 있게 한다.
 */
function extractExtension(input: string | null | undefined): string | null {
  if (!input) return null;
  const withoutQuery = input.split(/[?#]/)[0] ?? "";
  const last = withoutQuery.split("/").pop() ?? "";
  const dot = last.lastIndexOf(".");
  if (dot < 0 || dot === last.length - 1) return null;
  return last.slice(dot + 1).toLowerCase();
}

/** MIME → 확장자 폴백. FilePreviewModal 이 fileName 확장자로 PDF 를 판별하므로 확장자를 얻어와야 한다. */
const MIME_TO_EXT: Record<string, string> = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "image/bmp": "bmp",
};

/**
 * "가장 신뢰할 수 있는" 확장자 산출 — contentType → fileName → fileUrl 순서.
 * FilePreviewModal 이 fileName 확장자로 PDF/이미지 판별하므로, 여기서 확장자를 확정해
 * 아래 `resolveDisplayFileName` 에 반드시 확장자가 있는 이름을 넘긴다.
 */
function resolveEffectiveExtension(
  contentType: string | null | undefined,
  fileName: string | null | undefined,
  fileUrl: string | null | undefined,
): string | null {
  if (contentType) {
    const mapped = MIME_TO_EXT[contentType.toLowerCase()];
    if (mapped) return mapped;
  }
  return extractExtension(fileName) ?? extractExtension(fileUrl);
}

// (2026-08-03) 정적 판별기(isImageFile / detectIsImage / isCmarketPartnerFileUrl / IMAGE_EXTS)
// 는 백엔드 프록시(magic-number 판별) 도입으로 대체돼 삭제. resolveEffectiveExtension /
// MIME_TO_EXT 는 resolveDisplayFileName 이 확장자를 fileName 에 얹을 때 계속 사용됨.

export function ContractDocumentManagementCard({
  roomId,
  role,
  items,
  contractId,
  paymentChannel = null,
  isPaid = false,
  taxInvoiceStatus = null,
  onOpenCmarketContracts,
  restrictionConfirm,
}: ContractDocumentManagementCardProps) {
  // 결제 수단을 아는 계약에서만, 그리고 부모가 씨마켓으로 데려갈 수 있을 때만 CTA 를 그린다.
  // ContractCompleteCard 와 동일한 판정 — 역할 분기는 두지 않는다(주입 여부가 곧 게이트).
  const payChannel = onOpenCmarketContracts ? paymentChannel : null;
  // 진행 상태는 **버튼과 다른 게이트**를 쓴다. 버튼을 그릴 수 없는 화면(공급사·임베드 밖)
  // 에서도 계산서가 발행됐는지는 보여야 한다.
  const settlementBadge = resolveContractSettlementBadge({
    paymentChannel,
    isPaid,
    taxInvoiceStatus,
  });
  const isSupplier = role === "supplier";
  const {
    onUploadContractDocumentFile,
    onSubmitContractDocumentInput,
    onSubmitContractDocumentBuyerConfirmation,
  } = useChatActions();

  // auto_pending_input 은 자동 생성 완료된 파일과 함께 렌더링(완료 상태에선 auto 와 동일)
  const autoItems = useMemo(
    () =>
      items.filter(
        (doc) =>
          doc.kind === "auto" ||
          (doc.kind === "auto_pending_input" && doc.status === "completed"),
      ),
    [items],
  );
  const pendingInputItems = useMemo(
    () =>
      items.filter(
        (doc) => doc.kind === "auto_pending_input" && doc.status === "pending",
      ),
    [items],
  );
  const manualItems = useMemo(
    () => items.filter((doc) => doc.kind === "manual"),
    [items],
  );
  const completedItems = useMemo(
    () => items.filter((doc) => doc.status === "completed" && doc.fileUrl),
    [items],
  );
  const pendingManualCount = manualItems.filter(
    (doc) => doc.status === "pending",
  ).length;

  const allManualCompleted =
    manualItems.length > 0 && pendingManualCount === 0;

  // "예"로 응답된 문항 인덱스 → 문항 문구. 알 수 없는 인덱스(서식 개정 등)는 번호로 폴백.
  const flaggedIndexes = restrictionConfirm?.flaggedIndexes ?? [];
  const flaggedQuestions = useMemo(
    () =>
      flaggedIndexes.map(
        (index) =>
          CONTRACT_RESTRICTION_QUESTIONS[index] ?? `${String(index + 1)}번 문항`,
      ),
    [flaggedIndexes],
  );

  // paperCode 별 모달 open 상태 (한 번에 하나만 열림)
  const [openModalPaperCode, setOpenModalPaperCode] = useState<number | null>(
    null,
  );
  // paperCode 135 buyer 확인 모달 open 상태 (별도 — supplier 모달과 겹치지 않는다)
  const [buyerConfirmModalOpen, setBuyerConfirmModalOpen] = useState(false);
  // 미리보기 모달 상태.
  // 백엔드 프록시가 magic-number 로 판별한 Content-Type 이 그대로 blob.type 에 실려온다.
  // objectUrl 은 닫힐 때 revoke. 원본 doc 은 파일명·다운로드 URL 유지용.
  const [preview, setPreview] = useState<
    | { doc: ContractDocumentItem; objectUrl: string; isImage: boolean }
    | null
  >(null);

  const buyerConfirmation = restrictionConfirm?.buyerConfirmation ?? null;
  // buyer 확인이 필요한 상태: "예" 응답이 있고 아직 확정 응답이 없을 때
  const needsBuyerConfirmation =
    flaggedIndexes.length > 0 && buyerConfirmation === null;

  // 미리보기 클릭 — 백엔드 프록시(`/chat/rooms/:roomId/contract-documents/:paperCode/preview`)
  // 로부터 blob 을 받아 modal 에 넘긴다. 프록시가 magic-number 판별로 정확한 Content-Type 을
  // 얹어 스트리밍하므로, blob.type 만으로 이미지/PDF 결정이 가능하고 c-market Accept-협상,
  // Supabase attachment 헤더, CORS 등 서버 편차를 전부 우회한다.
  const handlePreview = async (doc: ContractDocumentItem) => {
    if (!doc.fileUrl) return;
    try {
      const res = await apiClient.get<Blob>(
        `/chat/rooms/${roomId}/contract-documents/${String(doc.paperCode)}/preview`,
        { responseType: "blob" },
      );
      const blob = res.data;
      const objectUrl = URL.createObjectURL(blob);
      setPreview({
        doc,
        objectUrl,
        isImage: blob.type.startsWith("image/"),
      });
    } catch {
      // 원본 file_url 로 폴백하지 않는다. 저장된 값에는 만료가 있고(견적서는 1시간
      // 서명 URL), 앵커를 걸면 브라우저가 Supabase 의 400(jwt expired) JSON 을 그대로
      // 띄운다 — 우리 실패를 남의 에러 페이지로 보여주는 셈이었다. 프록시가 못 열면
      // 사용자에게 못 열었다고 말한다.
      toast.error(`${doc.name} 미리보기에 실패했습니다.`);
    }
  };

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.objectUrl);
    setPreview(null);
  };

  /**
   * 다운로드는 반드시 백엔드 프록시를 탄다.
   *
   * 원본 file_url 로 앵커를 걸면 씨마켓 `/files/{id}` 는 Bearer 토큰이 헤더에 없어 401 로
   * 죽는다 — 토큰이 URL 안에 들어 있는 Supabase 서명 URL(공급사 직접 제출분)만 살아남아
   * "일괄 다운로드에 사업자등록증·통장사본만 담긴다" 는 증상이 됐다. 프록시는 프리뷰와
   * 같은 경로로 서버가 토큰을 붙여 받아온 뒤 attachment 로 내려준다.
   */
  const fetchDocumentBlob = async (
    doc: ContractDocumentItem,
  ): Promise<Blob> => {
    const res = await apiClient.get<Blob>(
      `/chat/rooms/${roomId}/contract-documents/${String(doc.paperCode)}/download`,
      { responseType: "blob" },
    );
    return res.data;
  };

  const saveBlob = (blob: Blob, fileName: string) => {
    const objectUrl = URL.createObjectURL(blob);
    triggerDownload(objectUrl, fileName);
    // 앵커 클릭이 처리된 뒤에 해제한다 — 즉시 revoke 하면 저장이 취소되는 브라우저가 있다.
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  };

  const handleDownload = async (doc: ContractDocumentItem) => {
    if (!doc.fileUrl) return;
    try {
      saveBlob(await fetchDocumentBlob(doc), doc.fileName ?? doc.name);
    } catch {
      toast.error(`${doc.name} 다운로드에 실패했습니다.`);
    }
  };

  const [isBatchDownloading, setIsBatchDownloading] = useState(false);

  const handleBatchDownload = async () => {
    if (isBatchDownloading) return;
    setIsBatchDownloading(true);
    const failed: string[] = [];
    try {
      // 순차 처리 — 브라우저가 동시 다운로드를 억제하고, 실패 건을 정확히 집어내기 위해서다.
      for (const doc of completedItems) {
        if (!doc.fileUrl) continue;
        try {
          saveBlob(await fetchDocumentBlob(doc), doc.fileName ?? doc.name);
        } catch {
          failed.push(doc.name);
        }
        await new Promise((resolve) => window.setTimeout(resolve, 250));
      }
    } finally {
      setIsBatchDownloading(false);
    }
    // 조용히 일부만 받아지는 것이 이번 증상의 본질이었다 — 실패는 반드시 알린다.
    if (failed.length > 0) {
      toast.error(`${failed.join(", ")} 다운로드에 실패했습니다.`);
    }
  };

  return (
    <div className="w-full flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 bg-slate-50 flex items-center gap-2 border-b border-slate-100">
        <FileText className="w-4 h-4 text-slate-600" />
        <span className="text-sm font-semibold text-slate-800">
          계약 서류 관리
        </span>
      </div>

      {/* 수의계약 체결 제한 "예" 응답 경고 — buyer/supplier 모두에게 상시 노출 */}
      {flaggedQuestions.length > 0 && (
        <div className="mx-4 mt-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-3">
          <div className="flex items-center gap-1.5">
            <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-sm font-semibold text-amber-900">
              수의계약 체결 여부 확인 필요 ({flaggedQuestions.length}건)
            </span>
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-amber-800">
            {isSupplier
              ? needsBuyerConfirmation
                ? '아래 항목에 "예"로 응답해 발주기관 최종 확인을 기다리는 중입니다.'
                : '아래 항목에 "예"로 응답하여 발주기관에 통보되었습니다.'
              : needsBuyerConfirmation
                ? "공급사가 아래 항목에 \"예\"로 응답했습니다. 내용을 확인 후 최종 응답을 제출해 주세요."
                : `발주기관 최종 확인: ${buyerConfirmation === "YES" ? "예 — 계약 진행" : "아니오 — 계약 미진행"}`}
          </p>
          <ul className="mt-2 space-y-1">
            {flaggedQuestions.map((question, idx) => (
              <li
                key={flaggedIndexes[idx]}
                className="text-xs leading-relaxed text-amber-900 pl-3 relative before:content-['·'] before:absolute before:left-0.5"
              >
                {question}
              </li>
            ))}
          </ul>
          {!isSupplier &&
            needsBuyerConfirmation &&
            onSubmitContractDocumentBuyerConfirmation && (
              <div className="mt-3 flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setBuyerConfirmModalOpen(true)}
                >
                  확인하기
                </Button>
              </div>
            )}
        </div>
      )}

      <div className="p-4 space-y-5">
        {/* [자동 생성] Section */}
        {autoItems.length > 0 && (
          <section>
            <SectionHeader
              icon={<Zap className="w-3.5 h-3.5 text-primary-text" />}
              label="자동 생성"
            />
            <ul className="mt-2 space-y-1.5">
              {autoItems.map((doc) => (
                <AutoDocumentRow
                  key={doc.id}
                  doc={doc}
                  onPreview={handlePreview}
                  onDownload={handleDownload}
                />
              ))}
            </ul>
          </section>
        )}

        {/* [업체 확인 필요] Section — auto_pending_input & pending */}
        {pendingInputItems.length > 0 && (
          <section>
            <SectionHeader
              icon={<ShieldAlert className="w-3.5 h-3.5 text-rose-500" />}
              label="업체 확인 필요"
              suffix={
                <span className="text-2xs font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                  {pendingInputItems.length}개 확인 대기
                </span>
              }
            />
            <ul className="mt-2 space-y-1.5">
              {pendingInputItems.map((doc) => (
                <PendingInputDocumentRow
                  key={doc.id}
                  doc={doc}
                  isSupplier={isSupplier}
                  onOpenInput={(paperCode) => setOpenModalPaperCode(paperCode)}
                />
              ))}
            </ul>
          </section>
        )}

        {/* [직접 제출] Section */}
        {manualItems.length > 0 && (
          <section>
            <SectionHeader
              icon={
                allManualCompleted ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                ) : (
                  <Clock className="w-3.5 h-3.5 text-amber-500" />
                )
              }
              label="직접 제출"
              suffix={
                <span
                  className={cn(
                    "text-2xs font-semibold px-2 py-0.5 rounded-full",
                    allManualCompleted
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-amber-50 text-amber-700 border border-amber-200",
                  )}
                >
                  {allManualCompleted
                    ? "제출 완료"
                    : `${pendingManualCount}개 제출 대기중`}
                </span>
              }
            />
            <ul className="mt-2 space-y-1.5">
              {manualItems.map((doc) => (
                <ManualDocumentRow
                  key={doc.id}
                  doc={doc}
                  roomId={roomId}
                  isSupplier={isSupplier}
                  onUpload={onUploadContractDocumentFile}
                  onPreview={handlePreview}
                  onDownload={handleDownload}
                />
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* 대금 정리 상태 — 현금은 계산서 발행 상태, 카드는 결제 완료 여부. 양쪽 역할 모두 본다. */}
      {paymentChannel !== null && settlementBadge && (
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2">
            <span className="text-xs font-semibold text-slate-700">
              {paymentChannel === "cash" ? "세금계산서" : "카드 결제"}
            </span>
            <ContractSettlementBadgeChip badge={settlementBadge} />
          </div>
        </div>
      )}

      {/* Batch download + 결제 footer */}
      {(completedItems.length > 0 || payChannel !== null) && (
        <div className="px-4 pb-4 flex flex-col sm:flex-row gap-2">
          {completedItems.length > 0 && (
            <Button
              type="button"
              variant="outline"
              className="flex-1 text-sm font-semibold rounded-xl"
              disabled={isBatchDownloading}
              onClick={() => void handleBatchDownload()}
            >
              <Download className="w-4 h-4 mr-1.5" />
              {isBatchDownloading
                ? "다운로드 중…"
                : `${String(completedItems.length)}개 생성된 서류 일괄 다운로드`}
            </Button>
          )}
          {payChannel !== null && (
            <Button
              type="button"
              className={cn(
                "flex-1 text-sm font-semibold rounded-xl",
                payChannel === "card" && isPaid && "opacity-70",
              )}
              // 현금은 계산서 발행요청이라 결제 완료로 잠그지 않는다 — 발행 요청은
              // 입금과 별개 절차다. 카드만 결제 완료 시 비활성화한다.
              disabled={payChannel === "card" && isPaid}
              onClick={() => onOpenCmarketContracts?.(contractId, payChannel)}
            >
              {payChannel === "cash"
                ? taxInvoiceStatus === "ISSUED"
                  ? "계산서 확인하기"
                  : taxInvoiceStatus === "REQUESTED"
                    ? "계산서 발행 현황 보기"
                    : "계산서 발행요청"
                : isPaid
                  ? "결제 완료"
                  : "카드결제하기"}
            </Button>
          )}
        </div>
      )}

      {/* auto_pending_input 서류 입력 모달 (한 번에 하나만 open) */}
      {openModalPaperCode === CONTRACT_RESTRICTION_PAPER_CODE && (
        <ContractRestrictionAllNoModal
          open={true}
          onOpenChange={(open) => {
            if (!open) setOpenModalPaperCode(null);
          }}
          onSubmit={async (answers) => {
            if (!onSubmitContractDocumentInput) return;
            await onSubmitContractDocumentInput(
              roomId,
              CONTRACT_RESTRICTION_PAPER_CODE,
              { contractRestrictionConfirm: { answers } },
            );
          }}
        />
      )}
      {openModalPaperCode !== null &&
        RETIREE_ROSTER_PAPER_CODES.has(openModalPaperCode) && (
          <OptionalRosterModal
            open={true}
            onOpenChange={(open) => {
              if (!open) setOpenModalPaperCode(null);
            }}
            paperCode={openModalPaperCode}
            onSubmit={async (rows) => {
              if (!onSubmitContractDocumentInput) return;
              await onSubmitContractDocumentInput(
                roomId,
                openModalPaperCode,
                // c-market spec: 해당없음 = { rows: [] } (빈 배열).
                // null 로 보내면 400 UPSTREAM_REJECTED — 블록 자체 생략과 구분 불가.
                { retireeRoster: { rows: rows ?? [] } },
              );
            }}
          />
        )}
      {/* buyer 최종 확인 모달 — 카드 배너 [확인하기] 로 진입 */}
      {onSubmitContractDocumentBuyerConfirmation && (
        <ContractRestrictionBuyerConfirmModal
          open={buyerConfirmModalOpen}
          onOpenChange={setBuyerConfirmModalOpen}
          answers={restrictionConfirm?.answers ?? []}
          flaggedIndexes={flaggedIndexes}
          onSubmit={async (buyerConfirmation) => {
            await onSubmitContractDocumentBuyerConfirmation(
              roomId,
              buyerConfirmation,
            );
          }}
        />
      )}
      {/*
        파일 미리보기 모달 — 백엔드 프록시가 magic-number 로 판별한 정확한 Content-Type 을
        얹어 스트리밍한 blob URL 을 modal 에 넘긴다. 다운로드도 이미 받아둔 그 blob 을 쓴다
        (원본 URL 로 트리거하면 씨마켓 파일은 토큰이 없어 401 로 죽는다).
      */}
      {preview && (
        <FilePreviewModal
          file={{
            url: preview.objectUrl,
            fileName: resolveDisplayFileName(preview.doc),
            fileSize: preview.doc.fileSize ?? 0,
            isImage: preview.isImage,
          }}
          onClose={closePreview}
          onDownload={() => {
            triggerDownload(
              preview.objectUrl,
              preview.doc.fileName ?? preview.doc.name,
            );
          }}
        />
      )}
    </div>
  );
}

/**
 * FilePreviewModal 에 넘길 표시 파일명 산출.
 * - 원본 fileName 에 확장자가 있으면 그대로 사용
 * - 없으면 contentType → URL 확장자 순으로 폴백해 이름 뒤에 부착
 * - 마지막 폴백으로 `.pdf` (c-market 생성 서류의 절대 다수가 PDF 라 iframe 이 열림)
 * (FilePreviewModal 내부 pdf/이미지 판별이 확장자에 의존하므로 확장자를 반드시 보전)
 */
function resolveDisplayFileName(doc: ContractDocumentItem): string {
  const rawName = doc.fileName ?? doc.name;
  if (extractExtension(rawName)) return rawName;
  const ext =
    resolveEffectiveExtension(doc.contentType, doc.fileName, doc.fileUrl) ??
    "pdf";
  return `${rawName}.${ext}`;
}

/* -------------------------------- sub-parts ------------------------------- */

function SectionHeader({
  icon,
  label,
  suffix,
}: {
  icon: React.ReactNode;
  label: string;
  suffix?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="inline-flex items-center gap-1.5">
        {icon}
        <span className="text-xs font-semibold text-slate-700">{label}</span>
      </div>
      {suffix}
    </div>
  );
}

function PendingInputDocumentRow({
  doc,
  isSupplier,
  onOpenInput,
}: {
  doc: ContractDocumentItem;
  isSupplier: boolean;
  onOpenInput: (paperCode: number) => void;
}) {
  return (
    <li className="flex items-center justify-between gap-2 px-3 py-2.5 border border-rose-200 rounded-lg bg-rose-50/40">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-800 truncate">
          {doc.name}
        </div>
        {!isSupplier && (
          <div className="text-2xs text-rose-600 mt-0.5">
            공급사 확인 후 자동 생성됩니다.
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {isSupplier ? (
          <IconTextButton
            icon={<PenLine className="w-3.5 h-3.5" />}
            label="정보 입력"
            onClick={() => onOpenInput(doc.paperCode)}
            variant="primary"
          />
        ) : (
          <span className="text-2xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200">
            업체 확인 필요
          </span>
        )}
      </div>
    </li>
  );
}

function AutoDocumentRow({
  doc,
  onPreview,
  onDownload,
}: {
  doc: ContractDocumentItem;
  onPreview: (doc: ContractDocumentItem) => void;
  onDownload: (doc: ContractDocumentItem) => Promise<void>;
}) {
  const disabled = !doc.fileUrl;
  return (
    <li className="flex items-center justify-between gap-2 px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50/40">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-800 truncate">
          {doc.name}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <IconTextButton
          icon={<Eye className="w-3.5 h-3.5" />}
          label="미리보기"
          disabled={disabled}
          onClick={() => onPreview(doc)}
        />
        <IconTextButton
          icon={<Download className="w-3.5 h-3.5" />}
          label="다운로드"
          disabled={disabled}
          onClick={() => {
            if (doc.fileUrl) void onDownload(doc);
          }}
        />
      </div>
    </li>
  );
}

function ManualDocumentRow({
  doc,
  roomId,
  isSupplier,
  onUpload,
  onPreview,
  onDownload,
}: {
  doc: ContractDocumentItem;
  roomId: string;
  isSupplier: boolean;
  onUpload?: (
    roomId: string,
    paperCode: number,
    file: File,
  ) => Promise<void>;
  onPreview: (doc: ContractDocumentItem) => void;
  onDownload: (doc: ContractDocumentItem) => Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const isCompleted = doc.status === "completed" && !!doc.fileUrl;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUpload) return;

    setUploadError(null);

    // 프론트 사전 검증 (SSOT 재확인 — 백엔드에서도 재검증됨)
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setUploadError("파일 크기가 10MB를 초과합니다");
      e.target.value = "";
      return;
    }
    if (file.name.length > MAX_FILENAME_LENGTH) {
      setUploadError("파일명이 너무 깁니다");
      e.target.value = "";
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setUploadError("지원하지 않는 확장자입니다");
      e.target.value = "";
      return;
    }

    setIsUploading(true);
    try {
      await onUpload(roomId, doc.paperCode, file);
    } catch (err) {
      setUploadError(
        err instanceof Error ? err.message : "파일 업로드에 실패했습니다",
      );
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <li
      className={cn(
        "flex items-center justify-between gap-2 px-3 py-2.5 border rounded-lg",
        isCompleted
          ? "border-emerald-200 bg-emerald-50/40"
          : "border-slate-200 bg-white",
      )}
    >
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "text-sm font-medium truncate",
            isCompleted ? "text-emerald-800" : "text-slate-800",
          )}
        >
          {doc.name}
        </div>
        {isCompleted && doc.fileName && (
          <div className="text-2xs text-slate-500 truncate mt-0.5">
            {doc.fileName}
            {doc.fileSize ? ` · ${humanFileSize(doc.fileSize)}` : ""}
          </div>
        )}
        {uploadError && (
          <div className="text-2xs text-red-500 mt-0.5">{uploadError}</div>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {!isCompleted && !isSupplier && (
          <span className="text-2xs font-semibold text-amber-600 px-2">
            제출 대기
          </span>
        )}
        {isCompleted && (
          <>
            <IconTextButton
              icon={<Eye className="w-3.5 h-3.5" />}
              label="미리보기"
              onClick={() => onPreview(doc)}
            />
            <IconTextButton
              icon={<Download className="w-3.5 h-3.5" />}
              label="다운로드"
              onClick={() => {
                if (doc.fileUrl) void onDownload(doc);
              }}
            />
          </>
        )}
        {isSupplier && (
          <>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              accept={ACCEPT_ATTR}
              onChange={(e) => {
                void handleFileSelect(e);
              }}
            />
            <IconTextButton
              icon={
                isCompleted ? (
                  <RefreshCw className="w-3.5 h-3.5" />
                ) : (
                  <Paperclip className="w-3.5 h-3.5" />
                )
              }
              label={
                isUploading
                  ? "업로드 중..."
                  : isCompleted
                    ? "재첨부"
                    : "파일 첨부"
              }
              disabled={isUploading || !onUpload}
              onClick={openPicker}
              variant={isCompleted ? "ghost" : "primary"}
            />
          </>
        )}
      </div>
    </li>
  );
}

function IconTextButton({
  icon,
  label,
  onClick,
  disabled,
  variant = "ghost",
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  variant?: "ghost" | "primary";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-1 h-7 px-2 rounded-md text-2xs font-semibold transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
        disabled && "opacity-40 cursor-not-allowed",
        variant === "primary" && !disabled
          ? "bg-primary text-primary-foreground hover:bg-primary-hover"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
